import type React from "react";
import {Button, LinearProgress, Stack, Typography} from "@mui/material";
import {useNavigate} from "react-router";
import {GreyPaper} from "~/components/GreyPaper/GreyPaper";
import {IconWrapper} from "~/components/IconWrapper/IconWrapper";
import ValueChainIcon from "~/icons/svg/ValueChainIcon.svg?react";
import InfoIcon from '~/icons/svg/InfoIcon.svg?react';
import type {ChainCreditEntitlement} from "~/pages/supplier-360/tabs/SupplierValueChain/valueChainCreditTypes";
import {creditTone, creditsUsed, formatCreditDate} from "~/pages/supplier-360/tabs/SupplierValueChain/chainCredits";

type ChainCreditsStripProps = {
    entitlement: ChainCreditEntitlement;
};

/** The balance strip above every Value chain state (`ChainCreditsStrip` in the design). */
export const ChainCreditsStrip: React.FC<ChainCreditsStripProps> = ({entitlement}) => {
    const navigate = useNavigate();
    const granted = entitlement.credits_granted;
    const remaining = entitlement.credit_balance;
    const used = creditsUsed(entitlement);
    const pct = granted ? Math.min(100, (used / granted) * 100) : 100;
    const validTo = entitlement.expires_at ? ` · valid to ${formatCreditDate(entitlement.expires_at)}` : '';

    return (
        <GreyPaper sx={{mb: 2}}>
            <Stack direction="row" alignItems="center" gap={1.5}>
                <IconWrapper color="secondary">
                    <ValueChainIcon/>
                </IconWrapper>
                <Stack>
                    <Typography variant="body2" fontWeight={600}>Value chain credits</Typography>
                    <Typography variant="caption" color="text.secondary">
                        {granted ? `${remaining} of ${granted} company credits left${validTo}` : 'No credits on your plan'}
                    </Typography>
                </Stack>
                <LinearProgress
                    variant="determinate"
                    value={pct}
                    color={creditTone(entitlement)}
                    sx={{width: 140, height: 6, borderRadius: 3, marginLeft: 'auto'}}
                />
                <Typography variant="body2" fontWeight={500} sx={{whiteSpace: 'nowrap'}}>
                    <b>{used}</b> / {granted} used
                </Typography>
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<InfoIcon fontSize="small"/>}
                    onClick={() => navigate('credits')}
                    sx={{backgroundColor: 'white !important'}}
                >
                    Manage credits
                </Button>
            </Stack>
        </GreyPaper>
    );
};
